<template>
  <ica-modal
    v-model="activeInner"
    :title="title"
    :z-index="200"
    :size="size"
    :class="modalView"
  >
    <template #content>
      <add-single-recipe-to-folder-modal-content
        v-if="!newFolderMode && totalFolderCount"
        :folders="folders"
        :loading="loading"
        :total-folder-count="totalFolderCount"
        :loading-more="loadingMore"
        @save-to-folder="saveToFolder"
        @create-new-folder="createNewFolder"
        @load-more="loadMore"
      />
      <create-folder-modal-content
        v-else
        :collection-created="collectionCreated"
        :show-error-message="showErrorMessage"
        @close-modal="closeModal"
        @save-new-collection="saveNewCollection"
        @route-to-collection="routeToCollection"
      />
    </template>
  </ica-modal>
</template>

<script>
import { IcaModal } from '~/@ica/ica-molecules-vue/src/components'
import AddSingleRecipeToFolderModalContent from './AddSingleRecipeToFolderModalContent.vue'
import CreateFolderModalContent from './CreateFolderModalContent.vue'
import datalayer from '../../../analytics/dataLayer'

export default {
  name: 'IcaAddSingleRecipeToFolderModal',
  components: {
    IcaModal,
    AddSingleRecipeToFolderModalContent,
    CreateFolderModalContent,
  },
  props: {
    recipeId: {
      type: Number,
      default: null,
    },
    recipeName: {
      type: String,
      default: '',
    },
    active: {
      type: Boolean,
      default: false,
    },
    recipeBaseUrl: {
      type: String,
      default: '',
    },
    getAccessTokenFn: {
      type: Function,
      default: () => {
        throw new Error('getAccessTokenFn not implemented in IcaAddSingleRecipeToFolderModal')
      },
    },
    fetchFoldersFn: {
      type: Function,
      default: function () {
        return this.fetch(`${this.recipeBaseUrl}/folders?skip=0&take=10`)
      },
    },
    fetchFolderCountFn: {
      type: Function,
      default: function () {
        return this.fetch(`${this.recipeBaseUrl}/folders/count`)
      },
    },
    saveToFolderFn: {
      type: Function,
      default: function (folderId) {
        return this.fetch(`${this.recipeBaseUrl}/folders/${folderId}/items`, {
          method: 'POST',
          body: JSON.stringify({
            recipeIds: [this.recipeId],
          }),
        })
      },
    },
    saveNewCollectionFn: {
      type: Function,
      default: function (folderName) {
        return this.fetch(`${this.recipeBaseUrl}/folders`, {
          method: 'POST',
          body: JSON.stringify({
            folderName,
            recipeIds: [this.recipeId],
          }),
        })
      },
    },
    loadMoreFoldersFn: {
      type: Function,
      default: function (skip) {
        return this.fetch(`${this.recipeBaseUrl}/folders?skip=${skip}&take=10`)
      },
    },
  },
  data () {
    return {
      totalFolderCount: 0,
      folders: [],
      newFolderMode: false,
      showErrorMessage: false,
      collectionCreated: false,
      createdFolderId: null,
      loadingMore: false,
      loading: true,
    }
  },
  computed: {
    activeInner: {
      get () {
        return this.active
      },
      set (val) {
        this.$emit('close-modal', val)
      },
    },
    modalView () {
      if (this.newFolderMode) {
        return 'createFolderModal'
      }
      return 'addSingleRecipeToFolderModal'
    },
    size () {
      if (this.newFolderMode || !this.totalFolderCount) {
        return 'medium'
      }
      return 'large'
    },
    title () {
      if (this.collectionCreated) {
        return 'Receptsamling skapad'
      } else if (this.newFolderMode) {
        return 'Skapa receptsamling'
      } else {return 'Lägg till i receptsamling'}
    },
  },
  watch: {
    activeInner (val) {
      if (val) {
        this.fetchFolderCount()
        this.fetchFolders()
        this.openModalGa()
      } else {
        this.collectionCreated = false
        this.newFolderMode = false
      }
    },
  },
  mounted () {
    this.fetchFolderCount()
  },
  methods: {
    async fetch (url, options = {}) {
      try {
        const token = await this.getAccessTokenFn()
        options = {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
        return await (await fetch(url, options)).json()
      } catch (error) {
        console.log(error)
      }
    },
    fetchFolders () {
      this.loading = true
      this.fetchFoldersFn().then((data) => {
        this.folders = data
        this.loading = false
      })
    },
    fetchFolderCount () {
      this.fetchFolderCountFn().then((data) => {
        this.totalFolderCount = data
        if (!this.totalFolderCount) {
          this.newFolderMode = true
        }
      })
    },
    closeModal () {
      this.$emit('close-modal')
      this.collectionCreated = false
      this.newFolderMode = false
    },
    createNewFolder () {
      this.newFolderMode = true
    },
    saveToFolder (folderId) {
      this.saveToFolderFn(folderId)
      .then(() => {
        this.$emit('save-to-folder')
        this.addRecipesToFolderGa(folderId, true)
      })
      .catch(() => {
        this.addRecipesToFolderGa(folderId, false)
      })
    },
    saveNewCollection (folderName) {
      this.loading = true
      this.saveNewCollectionFn(folderName)
        .then((data) => {
          if (data) {
            this.loading = false
            this.createdFolderId = data.id
            this.collectionCreated = true
            this.totalFolderCount++
            this.createFolderGa(data.id, true)
            this.addRecipesToFolderGa(data.id, true)
          }
        })
        .catch(() => {
          this.showErrorMessage = true
          this.createFolderGa(null, false)
          this.addRecipesToFolderGa(null, false)
        })
    },
    routeToCollection () {
      window.location.href = `/recept/dina-recept/samling?folderId=${this.createdFolderId}`
    },
    loadMore () {
      this.loadingMore = true
      const skip = this.folders.length
      this.loadMoreFoldersFn(skip)
        .then((data) => {
          if (data) {
            this.folders = this.folders.concat(data)
          }
        })
        .finally(() => {
          this.loadingMore = false
        })
    },
    createFolderGa (folderId, succeeded) {
      datalayer.add({
        event: 'recipeListCreate',
        recipeListId: folderId,
        succeeded: succeeded ? '1' : '0',
        recipeId: this.recipeId || '',
        recipeName: this.recipeName || '',
      })
    },
    addRecipesToFolderGa (folderId, succeeded) {
      datalayer.add({
        event: 'recipeListAddRecipe',
        recipeListId: folderId,
        recipeId: this.recipeId,
        recipeName: this.recipeName,
        succeeded: succeeded ? '1' : '0',
      })
    },
    openModalGa () {
      datalayer.add({
        event: 'recipeListOpen',
        recipeId: this.recipeId,
        recipeName: this.recipeName,
      })
    }
  },
}
</script>

<style lang="scss">
// $icaAtomsFontPath: 'node_modules/@ica/ica-atoms/fonts';

// @import 'node_modules/@ica/ica-atoms/fonts/_baseTypography';
// @import 'node_modules/@ica/ica-atoms/variables/colors';

.addSingleRecipeToFolderModal {
  .modal-content {
    padding: 0 1rem 1.5rem;
    overflow: hidden;

    &__inner {
      height: 100%;

      @include media-breakpoint-down(sm) {
        padding-bottom: 5rem;
      }
    }

    @include media-breakpoint-up(md) {
      padding: 0 1.5rem 2rem;
    }
  }
}

.createFolderModal {
  .modal-content {
    padding: 1.5rem 1rem;
    overflow: hidden;

    @include media-breakpoint-up(md) {
      padding: 1.5rem 1.5rem 2rem;
    }

    &__inner {
      height: 100%;
    }
  }
}

.modal-content-inner {
  position: relative;
  height: 100%;
}

.modal-load-more {
  padding-bottom: 3rem;

  &__icon {
    width: 3rem;
    height: 3rem;
    fill: $color-secondary-cta-text;
  }
}

.modal-content-loading {
  padding-bottom: 100%;
  position: relative;
}

.modal-loader-icon {
  width: 5rem;
  height: 5rem;
  position: absolute;
  fill: $color-secondary-cta-text;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -100%);
}
</style>
